import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { privateApi } from './api';
import { getToken } from './auth.service';

/** A project as the backoffice lists it, deleted ones included. */
export type BackOfficeProject = {
    id: string;
    name: string;
    description: string | null;
    internalTeamOnlyLabel: string | null;
    softDelete: boolean;
    createdAt: string;
    sampleCount: number;
    scannerCount: number;
    imageCount: number;
    /** The project's own people: super admins are not counted. */
    memberCount: number;
};

const LIST_KEY = ['backOfficeProjects'];

export const useBackOfficeProjects = () =>
    useQuery({
        queryKey: LIST_KEY,
        queryFn: async () =>
            (await privateApi.get<BackOfficeProject[]>('/backoffice/project'))
                .data,
        enabled: !!getToken(),
    });

// Every change here shows on the list (its counts, its badges) and on the
// user side's project pages.
const useInvalidate = () => {
    const queryClient = useQueryClient();
    return (projectId?: string) => {
        queryClient.invalidateQueries({ queryKey: LIST_KEY });
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        if (projectId) {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            queryClient.invalidateQueries({
                queryKey: ['field-data', projectId],
            });
        }
    };
};

export const useCreateBackOfficeProject = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: async (data: {
            name: string;
            description?: string;
            internalTeamOnlyLabel?: string;
        }) =>
            (await privateApi.post<{ id: string }>('/backoffice/project', data))
                .data,
        onSuccess: () => invalidate(),
    });
};

export const useSetBackOfficeProjectDeleted = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: async ({
            projectId,
            deleted,
        }: {
            projectId: string;
            deleted: boolean;
        }) => {
            if (deleted) await privateApi.delete(`/project/${projectId}`);
            else await privateApi.patch(`/project/${projectId}/restore`);
        },
        onSuccess: (_, { projectId }) => invalidate(projectId),
    });
};

export const useDeleteBackOfficeProjectPermanently = () => {
    const invalidate = useInvalidate();
    return useMutation({
        mutationFn: async (projectId: string) => {
            await privateApi.delete(`/backoffice/project/${projectId}/permanent`);
        },
        onSuccess: () => invalidate(),
    });
};

/** Puts a scanner on a project: one not seen yet is created there, one on
 *  another project moves there, its sample there losing it. */
export const useAssignScanner = () => {
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            projectId,
            serialNumber,
        }: {
            projectId: string;
            serialNumber: string;
        }) => {
            await privateApi.post(`/project/${projectId}/scanner`, {
                serialNumber,
            });
        },
        onSuccess: (_, { projectId }) => {
            invalidate(projectId);
            queryClient.invalidateQueries({ queryKey: ['backOfficeScanners'] });
        },
    });
};

/** A new sample in a project, fed by no scanner until a hub ties one to it. */
export const useCreateBackOfficeSample = () => {
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            projectId,
            ...data
        }: {
            projectId: string;
            name: string;
            description?: string;
        }) => {
            await privateApi.post(`/project/${projectId}/sample`, data);
        },
        onSuccess: (_, { projectId }) => {
            invalidate(projectId);
            queryClient.invalidateQueries({ queryKey: ['backOfficeSamples'] });
        },
    });
};

